import React from "react";
import { Box, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

import palette from "../../../common/styles/palette";

/**
 * Os elementos de superfície das abas de leitura do Debriefing (Dados IA e
 * Feedback), como no conceito (hiplade.lovable.app/debriefing): card branco,
 * cabeçalho de seção, pill de estado, faixa de aviso e linha de ficha
 * técnica.
 *
 * A aba falava quatro dialetos ao mesmo tempo — `TitleForm` com título de
 * página e `<Divider>`, `<Alert>` do MUI, listas rótulo/valor soltas sobre o
 * fundo e cards já no padrão do conceito. Cada bloco novo escolhia um. Aqui
 * fica o vocabulário único: quem escrever a próxima seção compõe, não
 * redesenha.
 */

export type Tom = "marca" | "sucesso" | "atencao" | "perigo" | "neutro";

const TONS: Record<Tom, { texto: string; fundo: string; borda: string }> = {
  marca: {
    texto: palette.brandDark,
    fundo: palette.brandSoft,
    borda: palette.lilacMid,
  },
  sucesso: {
    texto: palette.emeraldDeep,
    fundo: palette.emeraldSoft,
    borda: palette.emeraldStrong,
  },
  atencao: {
    texto: palette.warning,
    fundo: palette.warningSoft,
    borda: palette.warning,
  },
  perigo: {
    texto: palette.dangerText,
    fundo: palette.dangerSoft,
    borda: palette.dangerBorder,
  },
  neutro: {
    texto: palette.textMuted,
    fundo: palette.muted,
    borda: palette.border,
  },
};

/** A superfície de conteúdo da aba: branca, 1px de fio, sombra rasa. */
export const AiCard: React.FC<{
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}> = ({ children, sx }) => (
  <Box
    sx={{
      p: 2.5,
      mb: 2.5,
      borderRadius: `${palette.radius}px`,
      backgroundColor: palette.surface,
      border: `1px solid ${palette.border}`,
      boxShadow: palette.shadowSm,
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * Cabeçalho de seção: ícone da marca, título e a linha que diz para que serve.
 * `acao` é o canto direito (botão de download, chip de estado).
 */
export const AiSectionHeader: React.FC<{
  icone?: React.ReactNode;
  titulo: string;
  subtitulo?: React.ReactNode;
  acao?: React.ReactNode;
  sx?: SxProps<Theme>;
}> = ({ icone, titulo, subtitulo, acao, sx }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 2,
      flexWrap: "wrap",
      mb: 2.5,
      ...sx,
    }}
  >
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
      {icone}
      <Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: palette.brandDark, lineHeight: 1.25 }}
        >
          {titulo}
        </Typography>
        {subtitulo && (
          <Typography variant="body2" sx={{ color: palette.textMuted, maxWidth: 760 }}>
            {subtitulo}
          </Typography>
        )}
      </Box>
    </Box>
    {acao}
  </Box>
);

/** Rótulo de estado: caixa alta, curto, sempre um tom do conceito. */
export const AiPill: React.FC<{
  label: React.ReactNode;
  tom?: Tom;
  /** Sólido em vez de suave — só para o que precisa gritar (crítico falhado). */
  solido?: boolean;
}> = ({ label, tom = "neutro", solido = false }) => {
  const cores = TONS[tom];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 20,
        px: 0.875,
        borderRadius: "6px",
        fontSize: "0.6875rem",
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: "0.03em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        backgroundColor: solido ? cores.texto : cores.fundo,
        color: solido ? palette.brandForeground : cores.texto,
      }}
    >
      {label}
    </Box>
  );
};

/** Faixa de aviso: o que o professor precisa saber antes de usar o número. */
export const AiCallout: React.FC<{
  tom: Tom;
  titulo?: React.ReactNode;
  icone?: React.ReactNode;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}> = ({ tom, titulo, icone, children, sx }) => {
  const cores = TONS[tom];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        px: 2,
        py: 1.5,
        mb: 2.5,
        borderRadius: "8px",
        backgroundColor: cores.fundo,
        border: `1px solid ${cores.borda}`,
        ...sx,
      }}
    >
      {icone}
      <Box>
        {titulo && (
          <Typography variant="body2" sx={{ fontWeight: 700, color: cores.texto }}>
            {titulo}
          </Typography>
        )}
        {children && (
          <Typography component="div" variant="body2" sx={{ color: palette.text }}>
            {children}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/** Linha de ficha técnica: rótulo apagado à esquerda, valor à direita. */
export const AiFichaLinha: React.FC<{
  rotulo: string;
  children: React.ReactNode;
}> = ({ rotulo, children }) => (
  <Box
    sx={{
      display: "flex",
      gap: 1.5,
      py: 0.6,
      flexWrap: "wrap",
      borderBottom: `1px solid ${palette.muted}`,
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Typography variant="body2" sx={{ color: palette.textMuted, minWidth: 210 }}>
      {rotulo}
    </Typography>
    <Typography
      component="div"
      variant="body2"
      sx={{ flex: 1, minWidth: 200, color: palette.text, wordBreak: "break-word" }}
    >
      {children}
    </Typography>
  </Box>
);

/** O botão roxo de download do conceito — `.docx` do relatório, hoje em dois lugares. */
export const AiDownloadButton: React.FC<{
  href: string;
  children: React.ReactNode;
}> = ({ href, children }) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    download
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      textDecoration: "none",
      borderRadius: "8px",
      px: 1.75,
      py: 0.75,
      fontSize: "0.8125rem",
      fontWeight: 700,
      textTransform: "uppercase",
      color: palette.brandForeground,
      backgroundColor: palette.brand,
      "&:hover": { backgroundColor: palette.brandDark },
    }}
  >
    {children}
  </Box>
);

/** Título de bloco dentro de um card (não abre seção nova). */
export const AiBlocoTitulo: React.FC<{
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}> = ({ children, sx }) => (
  <Typography
    sx={{
      fontSize: "0.8125rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: palette.textMuted,
      mb: 1.25,
      ...sx,
    }}
  >
    {children}
  </Typography>
);
